import { useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useLocation, useParams } from "react-router-dom";
import { BaseButton } from "src/components";
import GoToListButton from "src/components/GoToListButton";

import InvitationInfo from "../components/InvitationInfo";

import { ServiceTypes } from "src/api/adminMemo/adminmemo-types";
import { useApiOperation } from "src/api/hooks";
import { deleteVisitorGroupAsync } from "src/api/visitor/visitor-api";
import { VisitorFormData } from "src/api/visitor/visitor-types";
import AdminMemoV3 from "src/components/AdminMemoV3/AdminMemoV3";
import RegistrationInformation from "src/components/RegistrationInformation";
import ViewPC from "src/components/ViewPC";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import useNavigate from "src/hooks/usePartnerNavigate";
import { PagePath } from "src/pages/product/details";
import { useModal } from "src/recoil/modalState/hook";
import { useToast } from "src/recoil/toast/hook";
import { isMobilePath } from "src/utils";
import PermissionInfo from "../components/PermissionInfo";
import VisitInfo from "../components/VisitInfo";
import VisitorEditAccessGroup from "../components/VisitorEditAccessGroup";
import VisitorInfo from "../components/VisitorInfo";
import VisitorOnlyViewAccessGroup from "../components/VisitorOnlyViewAccessGroup";
import useGetPermissionAcGroup from "../hooks/useGetPermissionAcGroup";
import useVisitor from "../hooks/useVisitor";

function BasicInfoDetail() {
  const { contractApplyNumber, visitApplyNumber } = useParams();

  const navigate = useNavigate();
  const { isAuthority } = usePartnerAuthority();
  const { openToast } = useToast();
  const { setBaseModal } = useModal();

  const location = useLocation();
  const params = useParams();
  const { executeAsync: deleteVisitorGroup } = useApiOperation(deleteVisitorGroupAsync);

  const { buildingList, contractManage, visitorGroup } = useVisitor({
    contractApplyNumber: contractApplyNumber,
    visitApplyNumber: visitApplyNumber,
  });

  // 계약 관리 출입 설정 상세 조회(커스텀 권한 조회)
  // visitorDefaultAccessGroupYn 값이 false 일 때 커스텀 권한으로 커스텀 출입그룹 등록가능
  // visitorDefaultAccessGroupYn 값이 true 일 때 기본 권한으로 신청계약 출입그룹을 보여주기만함 (수정불가)
  const { visitorDefaultAccessGroupYn } = useGetPermissionAcGroup(Number(contractManage?.contractManageId));

  const useFormReturn = useForm<VisitorFormData>();
  const { control, setValue, reset } = useFormReturn;

  const {
    fields: formVisitorInfoList,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "visitorInfoList",
  });

  useEffect(() => {
    if (visitorGroup) {
      const { contractVisitorList, ...invitationInfo } = visitorGroup;
      console.log("invitationInfo", invitationInfo);
      reset({
        //빌딩id만 스트링 타입으로 변경
        invitationInfo: { ...invitationInfo, buildingId: invitationInfo.buildingId?.toString() },
        visitorInfoList: visitorGroup?.contractVisitorList,
      });
    }
  }, [visitorGroup, reset]);

  useEffect(() => {
    if (buildingList?.find((building) => building.isPrimary)?.buildingId) {
      //대표건물을 찾아서 id set
      const buildingId = String(buildingList?.find((building) => building.isPrimary)?.buildingId);
      setValue("invitationInfo.buildingId", buildingId);
    }
  }, [buildingList, setValue]);

  return (
    <>
      <div className="contents-container__scroll visitor-detail">
        <div className="contents-container__wrap">
          <form>
            <InvitationInfo useFormReturn={useFormReturn} pageType="detail" />
            <VisitInfo useFormReturn={useFormReturn} pageType="detail" />

            <PermissionInfo useFormReturn={useFormReturn} pageType="detail" />

            <VisitorInfo
              pageType="detail"
              append={append}
              remove={remove}
              control={control}
              formVisitorInfoList={formVisitorInfoList}
              useFormReturn={useFormReturn}
            />

            {/* 커스텀 권한이 있고 대기 상태일때만 편집 가능함 */}
            {visitorDefaultAccessGroupYn === false && <VisitorEditAccessGroup />}

            {visitorDefaultAccessGroupYn === true && <VisitorOnlyViewAccessGroup pageType="detail" />}
          </form>
        </div>

        <RegistrationInformation data={visitorGroup} />

        <AdminMemoV3 serviceId={Number(visitApplyNumber)} serviceType={ServiceTypes.SERVICE_INVITED_VISITOR_GROUP} />
        {isMobilePath(location.pathname) && (
          <>
            <div className="contents-container__btn-wrap">
              <div className="left-area d-flex">
                <GoToListButton />
                {isAuthority("d") && visitorGroup?.visitStatus !== "VISIT_DELETE" && (
                  <BaseButton
                    title="삭제"
                    className="no-outline color-white size-large text-red900 ml10"
                    onClick={() =>
                      setBaseModal({
                        isOpen: true,
                        title: "삭제하시겠습니까?",
                        onClick: async () => {
                          try {
                            await deleteVisitorGroup({ visitApplyNumber: visitApplyNumber! });
                            openToast({
                              content: `정상적으로 삭제 되었습니다.`,
                            });
                            navigate(PagePath.visitor.list);
                          } finally {
                            setBaseModal({
                              isOpen: false,
                            });
                          }
                        },
                        onClose: () => {
                          setBaseModal({
                            isOpen: false,
                          });
                        },
                        btnRightTitle: "확인",
                        btnLeftTitle: "취소",
                      })
                    }
                  />
                )}
              </div>
            </div>

            <ViewPC
              path={PagePath.visitor.detail
                .replace(":partnerId", String(params.partnerId))
                .replace(":visitApplyNumber", String(params.visitApplyNumber))
                .replace(":contractApplyNumber", String(params.contractApplyNumber))}
            />
          </>
        )}
      </div>
      {!isMobilePath(location.pathname) && (
        <div className="contents-container__btn-wrap">
          <div className="left-area d-flex">
            <GoToListButton />
          </div>
          <div className="right-area">
            {isAuthority("d") && visitorGroup?.visitStatus !== "VISIT_DELETE" && (
              <BaseButton
                title="삭제"
                className="color-white size-large "
                onClick={() =>
                  setBaseModal({
                    isOpen: true,
                    title: "삭제하시겠습니까?",
                    onClick: async () => {
                      try {
                        await deleteVisitorGroup({ visitApplyNumber: visitApplyNumber! });
                        openToast({
                          content: `정상적으로 삭제 되었습니다.`,
                        });
                        navigate(PagePath.visitor.list);
                      } finally {
                        setBaseModal({
                          isOpen: false,
                        });
                      }
                    },
                    onClose: () => {
                      setBaseModal({
                        isOpen: false,
                      });
                    },
                    btnRightTitle: "확인",
                    btnLeftTitle: "취소",
                  })
                }
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}
export default BasicInfoDetail;
